import * as React from 'react'
import { redirect } from 'react-router-dom'

import {
  FinanceSpendingModuleRoutesVisibilityDocument,
  PurchaseRequestDetailDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { buildNavigation } from 'modules/core/lib/react-router'

const ReroutingIndex = React.lazy(() => import('modules/finance/pages/ReroutingIndex'))

const RootPage = React.lazy(() => import('modules/procurement/pages/RootPage'))

const PurchaseOrdersPage = React.lazy(() => import('modules/procurement/pages/PurchaseOrdersPage'))

const PurchaseOrderDetailPage = React.lazy(
  () => import('modules/procurement/pages/PurchaseOrderDetailPage')
)

const PurchaseRequestDetailSummaryPage = React.lazy(
  () =>
    import('modules/procurement/pages/PurchaseRequestDetailPage/PurchaseRequestDetailSummaryPage')
)

const PurchaseRequestDetailAttachmentsPage = React.lazy(
  () =>
    import('modules/procurement/pages/PurchaseRequestDetailPage/PurchaseRequestDetailAttachmentsPage')
)

const PurchaseRequestDetailCommentsPage = React.lazy(
  () =>
    import('modules/procurement/pages/PurchaseRequestDetailPage/PurchaseRequestDetailCommentsPage')
)

const PurchaseRequestsPage = React.lazy(
  () => import('modules/procurement/pages/PurchaseRequestsPage')
)

const PurchaseRequestDetailHeader = React.lazy(
  () => import('modules/procurement/components/purchaseRequests/PurchaseRequestDetailHeader')
)

const procurementSubModuleRoutes = buildNavigation((navigation) => {
  return [
    {
      path: navigation.finance.spending.procurement.path,
      element: <RootPage />,
      handle: {
        meta: ({ watchQuery }) => {
          return watchQuery({
            query: FinanceSpendingModuleRoutesVisibilityDocument,
          }).map(({ data }) => {
            const {
              sidebarSeeExpensesProcurementPurchaseRequestSubLevelItem,
              sidebarSeeExpensesProcurementPurchaseOrderSubLevelItem,
            } = data.expenses._policies // todo change to procurement policies
            return {
              title: i18n.t('finance.modules.spending.procurement'),
              subNavigation: [
                {
                  id: 'purchase-requests',
                  title: i18n.t('procurement.purchase_requests.title'),
                  href: navigation.finance.spending.procurement.purchaseRequests.fullPath(),
                  isVisible: sidebarSeeExpensesProcurementPurchaseRequestSubLevelItem.can,
                },
                {
                  id: 'purchase-orders',
                  title: i18n.t('procurement.purchase_orders.title'),
                  href: navigation.finance.spending.procurement.purchaseOrders.fullPath(),
                  isVisible: sidebarSeeExpensesProcurementPurchaseOrderSubLevelItem.can,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <ReroutingIndex />,
        },
        {
          path: navigation.finance.spending.procurement.purchaseRequests.path,
          handle: {
            meta: () => ({
              title: i18n.t('procurement.purchase_requests.title'),
              illustration: 'money-spreadsheet',
            }),
          },
          children: [
            {
              index: true,
              element: <PurchaseRequestsPage />,
            },
            {
              path: navigation.finance.spending.procurement.purchaseRequests.detail.path,
              handle: {
                resource: true,
                meta: ({ watchQuery, params, session }) => {
                  return watchQuery({
                    query: PurchaseRequestDetailDocument,
                    variables: {
                      id: params.id ?? '',
                      companyId: session.company.id,
                      pending: true,
                      reviewerIds: session.employee?.id ? [session.employee.id] : undefined,
                    },
                    fetchPolicy: 'cache-and-network',
                  }).map(({ data }) => {
                    const purchaseRequest = data?.procurement?.purchaseRequest
                    const canManagePurchaseRequests =
                      data?.procurement?._policies.purchaseRequestManage.can
                    const canManagePurchaseOrder =
                      data?.procurement?._policies.purchaseOrderRead.can
                    const isProcurementV2FeatureEnabled =
                      !!data?.features?.featuresConnection?.nodes?.some(
                        (f) => f.name === 'procurement_v2' && f.enabled
                      )

                    return {
                      title: purchaseRequest?.description,
                      headerComponent: purchaseRequest ? (
                        <PurchaseRequestDetailHeader
                          purchaseRequest={purchaseRequest}
                          canManagePurchaseRequests={canManagePurchaseRequests}
                          canManagePurchaseOrder={canManagePurchaseOrder}
                          onlyOwnRequests={false}
                          isWorkflowsEnabled={isProcurementV2FeatureEnabled}
                        />
                      ) : null,
                      subNavigation: purchaseRequest && [
                        {
                          id: 'summary',
                          title: i18n.t('procurement.purchase_requests.detail.tabs.summary'),
                          href: navigation.finance.spending.procurement.purchaseRequests.detail.summary.fullPath(
                            {
                              id: purchaseRequest.id,
                            }
                          ),
                          isVisible: true,
                        },
                        {
                          id: 'attachments',
                          title: i18n.t('procurement.purchase_requests.detail.tabs.attachments'),
                          href: navigation.finance.spending.procurement.purchaseRequests.detail.attachments.fullPath(
                            {
                              id: purchaseRequest.id,
                            }
                          ),
                          isVisible: true,
                        },
                        {
                          id: 'comments',
                          title: i18n.t('procurement.purchase_requests.detail.tabs.comments'),
                          href: navigation.finance.spending.procurement.purchaseRequests.detail.comments.fullPath(
                            {
                              id: purchaseRequest.id,
                            }
                          ),
                          isVisible: true,
                        },
                      ],
                    }
                  })
                },
              },
              children: [
                {
                  index: true,
                  loader: ({ params }) => {
                    return redirect(
                      navigation.finance.spending.procurement.purchaseRequests.detail.summary.fullPath(
                        {
                          id: params.id ?? '',
                        }
                      )
                    )
                  },
                },
                {
                  path: navigation.finance.spending.procurement.purchaseRequests.detail.summary
                    .path,
                  element: <PurchaseRequestDetailSummaryPage />,
                },
                {
                  path: navigation.finance.spending.procurement.purchaseRequests.detail.attachments
                    .path,
                  element: <PurchaseRequestDetailAttachmentsPage />,
                },
                {
                  path: navigation.finance.spending.procurement.purchaseRequests.detail.comments
                    .path,
                  element: <PurchaseRequestDetailCommentsPage />,
                },
              ],
            },
          ],
        },
        {
          path: navigation.finance.spending.procurement.purchaseOrders.path,
          element: <PurchaseOrdersPage />,
          handle: {
            meta: () => ({
              title: i18n.t('procurement.purchase_orders.title'),
              illustration: 'money-spreadsheet',
            }),
          },
          children: [
            {
              path: navigation.finance.spending.procurement.purchaseOrders.detail.path,
              element: <PurchaseOrderDetailPage />,
            },
          ],
        },
      ],
    },
  ]
})

export default procurementSubModuleRoutes
